import { createContext, useContext, ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useChampionshipTenant, ChampionshipTenantConfig } from "@/modules/talent/hooks/useChampionshipTenant";

interface ChampionshipTenantContextValue {
  config: ChampionshipTenantConfig;
  isLoading: boolean;
  isMainInstance: boolean;
  slug: string | null;
  basePath: string;
}

const ChampionshipTenantContext = createContext<ChampionshipTenantContextValue | null>(null);

export function ChampionshipTenantProvider({ children }: { children: ReactNode }) {
  const { slug } = useParams<{ slug?: string }>();
  const tenant = useChampionshipTenant(slug);

  if (tenant.isLoading && !tenant.isMainInstance) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  const value: ChampionshipTenantContextValue = {
    config: tenant.config!,
    isLoading: tenant.isLoading,
    isMainInstance: tenant.isMainInstance,
    slug: tenant.slug,
    basePath: tenant.basePath,
  };

  return (
    <ChampionshipTenantContext.Provider value={value}>
      {children}
    </ChampionshipTenantContext.Provider>
  );
}

export function ChampionshipTenantOverride({ config: rawConfig, children }: { config: any; children: ReactNode }) {
  const parent = useContext(ChampionshipTenantContext);
  const routeSlug = rawConfig.subdomain || rawConfig.championship_slug || "opencode";
  const value: ChampionshipTenantContextValue = {
    config: {
      organization_id: rawConfig.organization_id || parent?.config?.organization_id || "",
      championship_name: rawConfig.championship_name || "OpenCode Championship",
      championship_slug: rawConfig.championship_slug || rawConfig.subdomain || "opencode",
      logo_url: rawConfig.logo_url || null,
      logo_dark_url: rawConfig.logo_dark_url || null,
      favicon_url: rawConfig.favicon_url || null,
      primary_color: rawConfig.primary_color || "#059669",
      secondary_color: rawConfig.secondary_color || "#0f172a",
      accent_color: rawConfig.accent_color || "#CED23C",
      hero_title: rawConfig.hero_title || null,
      hero_subtitle: rawConfig.hero_subtitle || null,
      rules_content: rawConfig.rules_content || null,
      registration_open: rawConfig.registration_open ?? true,
      max_participants: rawConfig.max_participants || 512,
      registration_offset: rawConfig.registration_offset || 0,
      rules_url: rawConfig.rules_url || null,
      contact_email: rawConfig.contact_email || null,
      email_from_name: rawConfig.email_from_name || null,
      email_from_address: rawConfig.email_from_address || null,
      meta_title: rawConfig.meta_title || null,
      meta_description: rawConfig.meta_description || null,
      og_image_url: rawConfig.og_image_url || null,
      custom_css: rawConfig.custom_css || null,
      partnership_text: rawConfig.partnership_text || null,
      restrict_email_domain: rawConfig.restrict_email_domain ?? false,
      allowed_email_domains: rawConfig.allowed_email_domains || [],
      hero_bg_image_url: rawConfig.hero_bg_image_url || null,
      hero_badge_image_url: rawConfig.hero_badge_image_url || null,
      cta_text: rawConfig.cta_text || null,
      cta_secondary_text: rawConfig.cta_secondary_text || null,
      prizes_config: rawConfig.prizes_config || null,
      how_it_works_config: rawConfig.how_it_works_config || null,
      sponsor_logos: rawConfig.sponsor_logos || null,
      faq_config: rawConfig.faq_config || null,
    },
    isLoading: false,
    isMainInstance: false,
    slug: routeSlug,
    basePath: `/opencode/${routeSlug}`,
  };

  return (
    <ChampionshipTenantContext.Provider value={value}>
      {children}
    </ChampionshipTenantContext.Provider>
  );
}

export function useChampionshipTenantContext() {
  const ctx = useContext(ChampionshipTenantContext);
  if (!ctx) {
    throw new Error("useChampionshipTenantContext must be used within ChampionshipTenantProvider");
  }
  return ctx;
}
